/**
 * FluencyScreen - K Part Whole Fluency Practice
 *
 * Timed fluency practice with countdown timer.
 * Student must answer within 6 seconds to build speed.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { useKTTS } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { getAccommodationTimerDuration, isAccommodationTimerUntimed } from '../../../policies/studentAccommodationPolicy';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';
import { PRACTICE_BADGE_CONFIG, PRACTICE_PHASES } from '../shared';

interface FluencyScreenProps {
  settings: KPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];  // digit = emptyCount
  onComplete: () => void;
  onBack: () => void;
}

const FluencyScreen: React.FC<FluencyScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const timerDurationMs = getAccommodationTimerDuration(6000, user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const [currentFact, setCurrentFact] = useState<{ factId: string; digit: number } | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const factScoresRef = useRef(factScores);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(timerDurationMs);
  const [timerActive, setTimerActive] = useState(false);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [phase, setPhase] = useState<'showing' | 'question'>('showing');

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const isFirstQuestionRef = useRef(true);

  const { playTTSWithCallback, playChime, isTTSPlaying } = useKTTS();

  // Generate answer choices
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6); // 0-5
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const currentEmptyCount = currentFact?.digit;
  const filledCount = currentEmptyCount !== undefined ? 5 - currentEmptyCount : 0;

  // Keep factScoresRef in sync
  useEffect(() => {
    factScoresRef.current = factScores;
  }, [factScores]);

  // Initialize first fact
  useEffect(() => {
    if (!currentFact && factsToPractice.length > 0) {
      setCurrentFact(factsToPractice[0]);
    }
  }, [currentFact, factsToPractice]);

  // Initialize when fact changes
  useEffect(() => {
    if (currentEmptyCount !== undefined) {
      setAnswerChoices(generateAnswerChoices(currentEmptyCount));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setWrongAnswer(null);
      setTimeRemaining(timerDurationMs);
      setTimerActive(false);
      setPhase('showing');
      setQuestionStartTime(Date.now());
    }
  }, [currentEmptyCount, generateAnswerChoices, timerDurationMs]);

  // Start showing phase (skip delay on subsequent questions)
  useEffect(() => {
    if (phase === 'showing' && currentEmptyCount !== undefined) {
      if (isFirstQuestionRef.current) {
        const timeoutId = setTimeout(() => {
          setPhase('question');
        }, 600);
        timeoutsRef.current.push(timeoutId);
      } else {
        setPhase('question');
      }
    }
  }, [phase, currentEmptyCount]);

  // Play TTS and start timer when entering question phase (first question only)
  useEffect(() => {
    if (phase === 'question' && !ttsHasPlayed && currentEmptyCount !== undefined) {
      if (isFirstQuestionRef.current && settings.speechEnabled) {
        const audioFile = {
          filename: 'kAssessment/partWhole/k_partwhole_instruction.mp3',
          text: 'How many empty circles?'
        };

        playTTSWithCallback(audioFile, () => {
          setTtsHasPlayed(true);
          setTimerActive(true);
        }).catch(() => {
          setTtsHasPlayed(true);
          setTimerActive(true);
        });
      } else {
        setTtsHasPlayed(true);
        setTimerActive(true);
      }
    }
  }, [phase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, currentEmptyCount]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // 6-second countdown timer
  useEffect(() => {
    if (isUntimed || !timerActive || hasAnswered || timeRemaining <= 0) {
      if (timeRemaining <= 0 && !hasAnswered && currentFact) {
        // Time expired - treat as incorrect
        setHasAnswered(true);
        setTimerActive(false);
        setTimeRemaining(timerDurationMs);

        // handleAnswerSubmission handles progress reporting - no need to duplicate here
        handleAnswerSubmission(false);
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [isUntimed, timeRemaining, timerActive, hasAnswered, currentFact, user, questionStartTime]);

  const advanceToNext = useCallback(() => {
    const latestScores = factScoresRef.current;
    const eligibleFacts = factsToPractice.filter(fact => {
      const score = latestScores.get(fact.factId) || 0;
      return score < 1;
    });

    if (eligibleFacts.length === 0) {
      onComplete();
      return;
    }

    isFirstQuestionRef.current = false;
    const randomIndex = Math.floor(Math.random() * eligibleFacts.length);
    const nextFact = eligibleFacts[randomIndex];
    setCurrentFact(nextFact);
    setAnswerChoices(generateAnswerChoices(nextFact.digit));

    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
    setWrongAnswer(null);
    setTimeRemaining(timerDurationMs);
    setTimerActive(false);
    setPhase('showing');
    setQuestionStartTime(Date.now());
  }, [factsToPractice, onComplete, generateAnswerChoices, timerDurationMs]);

  const handleAnswerSubmission = useCallback((isCorrect: boolean) => {
    if (!currentFact) return;

    setHasAnswered(true);
    setTimerActive(false);

    const responseTime = Date.now() - questionStartTime;

    if (user?.userId && currentFact) {
      try {
        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [currentFact.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: responseTime
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting K part whole fluency attempt:', error);
      }
    }

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        // Update to mastered when fluency target is met
        if (newScore === 1 && user?.userId) {
          const timeoutId = setTimeout(async () => {
            try {
              const progressUpdate: SetUserProgressRequest = {
                facts: {
                  [currentFact.factId]: {
                    status: 'mastered'
                  }
                }
              };
              await setUserProgress(user.userId, progressUpdate);
            } catch (error) {
              console.error('Failed to update fact status to mastered:', error);
            }
          }, 0);
          timeoutsRef.current.push(timeoutId);
        }

        return newScores;
      });

      playChime().then(() => {
        const timeoutId = setTimeout(() => {
          advanceToNext();
        }, 500);
        timeoutsRef.current.push(timeoutId);
      });
    } else {
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      const timeoutId = setTimeout(() => {
        setHasAnswered(false);
        setWrongAnswer(null);
      }, 1500);
      timeoutsRef.current.push(timeoutId);
    }
  }, [currentFact, playChime, advanceToNext, user, questionStartTime]);

  const handleAnswerClick = useCallback((selectedEmptyCount: number) => {
    if (isTTSPlaying || hasAnswered || !currentFact) return;

    const isCorrect = selectedEmptyCount === currentEmptyCount;

    if (!isCorrect) {
      setWrongAnswer(selectedEmptyCount);
      const timeoutId = setTimeout(() => setWrongAnswer(null), 1000);
      timeoutsRef.current.push(timeoutId);
    }

    handleAnswerSubmission(isCorrect);
  }, [currentEmptyCount, hasAnswered, isTTSPlaying, currentFact, handleAnswerSubmission]);

  if (!currentFact || currentEmptyCount === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row */}
      <div className="flex justify-between items-center p-6">
        <button
          onClick={onBack}
          className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
        >
          ← Back
        </button>

        <div className={`${PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].bgColor} ${PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].textColor} px-6 py-3 rounded-full font-bold text-lg shadow-lg`}>
          {PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].emoji} {PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].label}
        </div>

        {/* Timer Display */}
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" fill="none" stroke="#d1d5db" strokeWidth="2" />
            {timerActive && !isUntimed && (
              <circle
                cx="16" cy="16" r="14" fill="none"
                stroke={timeRemaining < 1000 ? "#dc2626" : "#f59e0b"}
                strokeWidth="2" strokeLinecap="round" strokeDasharray="87.96"
                strokeDashoffset={87.96 * (1 - (timeRemaining / timerDurationMs))}
                className="transition-all duration-100 ease-linear"
              />
            )}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-xs font-bold ${timeRemaining < 1000 ? 'text-red-600' : 'text-yellow-600'}`}>
              {isUntimed ? '∞' : timerActive ? Math.ceil(timeRemaining / 1000) : '⏱️'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-8">
        {/* 5-Ball Display */}
        <div className="flex gap-6 lg:gap-8">
          {[0, 1, 2, 3, 4].map((index) => (
            <motion.div
              key={index}
              initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
              animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
              transition={isFirstQuestionRef.current ? { duration: 0.3, delay: index * 0.05 } : { duration: 0.15 }}
              className={`
                w-16 h-16 lg:w-20 lg:h-20 rounded-full shadow-lg border-4
                ${index < filledCount
                  ? 'bg-blue-400 border-blue-500'
                  : 'bg-transparent border-dashed border-gray-400'
                }
              `}
            />
          ))}
        </div>

        {/* Showing Phase */}
        {phase === 'showing' && isFirstQuestionRef.current && (
          <div className="text-center">
            <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
              Be fast!
            </div>
          </div>
        )}

        {/* Question Phase - Answer Choices */}
        {phase === 'question' && (
          <div className="w-full max-w-md lg:max-w-lg">
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              {answerChoices.map((choice, index) => (
                <motion.button
                  key={choice}
                  initial={isFirstQuestionRef.current ? { opacity: 0, y: 30, rotate: -5 } : { opacity: 0 }}
                  animate={isFirstQuestionRef.current ? { opacity: 1, y: 0, rotate: 0 } : { opacity: 1 }}
                  transition={isFirstQuestionRef.current ? { duration: 0.4, delay: index * 0.1 } : { duration: 0.15 }}
                  whileHover={{ scale: 1.08, rotate: 1 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => handleAnswerClick(choice)}
                  disabled={hasAnswered}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                    ${wrongAnswer === choice
                      ? 'bg-red-500 text-white border-4 border-red-700 shadow-red-300 animate-pulse'
                      : hasAnswered && choice === currentEmptyCount
                      ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                      : hasAnswered && choice !== currentEmptyCount
                      ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                      : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700'
                    }
                    ${hasAnswered ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {choice}
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Quick answer!</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FluencyScreen;
